/**
 * @file: useEntityCardPicker.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Ergonomic entry point to the global entity-card picker (order /
 * service-record / report). Navigates to the `_entityCardPicker` modal
 * screen and resolves with the typed selection (or null if dismissed).
 * Usable from any screen.
 */

import { AppNavParams } from '@euler/app/Routes';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useMemo } from 'react';
import { EntityCardKind, EntityCardPickerResult } from './types';

export function useEntityCardPicker() {
  const navigation = useNavigation<StackNavigationProp<AppNavParams>>();
  const pick = useCallback(
    (kind: EntityCardKind) =>
      new Promise<EntityCardPickerResult | null>(resolve => {
        navigation.navigate('_entityCardPicker', { kind, onResult: resolve });
      }),
    [navigation],
  );
  return useMemo(
    () => ({
      pickOrder: () => pick('order'),
      pickServiceRecord: () => pick('service-record'),
      pickReport: () => pick('report'),
    }),
    [pick],
  );
}
